import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import CheckIcon from "@mui/icons-material/Check";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { Group } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/groups_pb";
import { Permission } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { DEFAULT_REGION_CODE } from "../../constants/index";
import settingsStyles from "./styles";
import { listGroups, listPermissions } from "api/endpoints/periscope-admin";
import { callOptionsHeaders } from "api/helpers";
import { regionValuesArr } from "api/index";
import { FilterTypeItem } from "components/shared/filter-type-autocomplete";
import {
  ActionTypeItemsFlat,
  actionTypeToString,
} from "constants/periscope-actions";
import { SnackbarContext } from "context/snackbar-context";
import downloadFile from "helpers/download-file";

const GRID_MAX_HEIGHT = "calc(100vh - 220px)";
const LABEL_COL_WIDTH = 280;

interface MatrixRow {
  type: "category" | "action";
  category?: string;
  item?: FilterTypeItem;
  key: string;
}

const PermissionMatrix: React.FC = () => {
  const { showError } = useContext(SnackbarContext);
  const settingsClasses = settingsStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [groups, setGroups] = useState<Group[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);

  const defaultRegion = useMemo(() => {
    return regionValuesArr.length === 1
      ? regionValuesArr[0]
      : DEFAULT_REGION_CODE;
  }, []);

  const permissionSet = useMemo(
    () => new Set(permissions.map((p) => `${p.groupId}:${p.actionType}`)),
    [permissions],
  );

  const matrixRows = useMemo((): MatrixRow[] => {
    const rows: MatrixRow[] = [];
    let lastCategory = "";
    let catIndex = 0;
    for (const item of ActionTypeItemsFlat) {
      if (item.Category !== lastCategory) {
        rows.push({
          type: "category",
          category: item.Category,
          key: `cat-${catIndex}-${item.Category}`,
        });
        lastCategory = item.Category;
        catIndex++;
      }
      rows.push({ type: "action", item, key: String(item.Value) });
    }
    return rows;
  }, []);

  const handleDownloadCsv = () => {
    try {
      const escapeCsv = (value: string) => {
        const sanitized = /^[=+\-@]/.test(value) ? `'${value}` : value;
        return sanitized.includes(",") ||
          sanitized.includes('"') ||
          /[\r\n]/.test(sanitized)
          ? `"${sanitized.replace(/"/g, '""')}"`
          : sanitized;
      };

      const header = [
        "Permission / Group",
        ...groups.map((g) => escapeCsv(g.name)),
      ].join(",");
      const dataRows = matrixRows.map((row) => {
        if (row.type === "category") {
          return [escapeCsv(row.category ?? ""), ...groups.map(() => "")].join(
            ",",
          );
        }
        const actionValue = row.item!.Value;
        const label = escapeCsv(actionTypeToString(actionValue as ActionType));
        const cells = groups.map((group) =>
          permissionSet.has(`${group.id}:${actionValue}`) ? "✓" : "",
        );
        return [label, ...cells].join(",");
      });

      const csvContent = [header, ...dataRows].join("\n");
      const fileName = `periscope-permission-matrix-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      downloadFile(fileName, csvContent);
    } catch (error) {
      showError(error as Parameters<typeof showError>[0]);
    }
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const headers = callOptionsHeaders(defaultRegion);
      const [fetchedGroups, fetchedPermissions] = await Promise.all([
        listGroups(headers),
        listPermissions(headers),
      ]);
      const sortedGroups = [...fetchedGroups].sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      setGroups(sortedGroups);
      setPermissions(fetchedPermissions);
    } catch (error) {
      showError(error as Parameters<typeof showError>[0]);
    } finally {
      setIsLoading(false);
    }
  }, [defaultRegion, showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (isLoading) {
    return (
      <CircularProgress
        color="primary"
        sx={settingsClasses.loadingSpinner}
        thickness={1}
        size={90}
      />
    );
  }

  if (groups.length === 0) {
    return (
      <Box sx={{ mt: 3, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          No groups found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3, width: "100%", pr: 3 }}>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: GRID_MAX_HEIGHT }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: (theme) => theme.zIndex.appBar + 1,
                    backgroundColor: "background.paper",
                    minWidth: LABEL_COL_WIDTH,
                    fontWeight: "bold",
                  }}
                >
                  Permission / Group
                  <Tooltip title="Export CSV">
                    <IconButton size="small" onClick={handleDownloadCsv}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                {groups.map((group) => (
                  <TableCell
                    key={group.id}
                    align="center"
                    sx={{ minWidth: 100 }}
                  >
                    {group.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {matrixRows.map((row) => {
                if (row.type === "category") {
                  return (
                    <TableRow key={row.key}>
                      <TableCell
                        colSpan={groups.length + 1}
                        sx={{
                          fontWeight: "bold",
                          backgroundColor: "action.hover",
                          position: "sticky",
                          left: 0,
                        }}
                      >
                        {row.category}
                      </TableCell>
                    </TableRow>
                  );
                }
                const actionValue = row.item!.Value;
                return (
                  <TableRow key={row.key} hover>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "background.paper",
                        zIndex: 1,
                        minWidth: LABEL_COL_WIDTH,
                      }}
                    >
                      {actionTypeToString(actionValue as ActionType)}
                    </TableCell>
                    {groups.map((group) => (
                      <TableCell key={group.id} align="center">
                        {permissionSet.has(`${group.id}:${actionValue}`) && (
                          <CheckIcon
                            fontSize="small"
                            color="success"
                            titleAccess={`${
                              group.name
                            } has ${actionTypeToString(
                              actionValue as ActionType,
                            )}`}
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default PermissionMatrix;
